import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UseFirmList } from "../../../../hooks/Catalogs/useFirm";
import { getCompanies } from "../../../../Services/Company";
import { getNotRegisteredGuestSpeakers } from "../../../../Services/GuestSpeaker";
import { fetchPersonByFirm } from "../../../../Services/Person";
import { getOfficeById } from "../../../../Services/Office";
import {
  checkPrepaidStatus,
  getRegistrationsByEvent,
} from "../../../../Services/Registration";
import type { AttendeeType } from "../../../../Enums/RegistrationEnum";
import { IRegistration } from "../../../../interfaces/models/IRegistration";
import { filterFirmsForRegistrantType } from "../../../../utils/wsgStaffFirms";
import type { AppDispatch } from "../../../../redux/store";
import { setAutoPrepaidLocked } from "../../../../redux/states/editParticipant.state";
import {
  selectEditParticipantForm,
  selectEventData,
  selectStaffFormState,
} from "../editParticipant.selectors";
import {
  applyStaffPrepaidSelection,
  clearStaffPrepaidSelection,
  isAnnualMeetingEvent,
  isBillableMemberType,
} from "../utils/participantPaymentRules";
import { isSoftDeletedPerson } from "../../../../utils/personSoftDelete";

export type RegistrantCatalogOption = {
  id: string;
  name: string;
  longName?: string;
};

type UseRegistrantCatalogOptionsParams = {
  eventId?: string;
  registrationData?: IRegistration;
  isEditMode: boolean;
  patchRegistration: (
    patch: Partial<IRegistration> & Record<string, unknown>,
  ) => void;
};

export function isGuestRegistrantType(attendeeType?: AttendeeType): boolean {
  return attendeeType === "invited_guest" || attendeeType === "speaker";
}

export function ensureSelectedOption(
  options: RegistrantCatalogOption[],
  selectedId?: string,
  selectedName?: string,
): RegistrantCatalogOption[] {
  if (!selectedId) {
    return options;
  }
  if (options.some((option) => option.id === selectedId)) {
    return options;
  }
  return [
    { id: selectedId, name: selectedName?.trim() || selectedId },
    ...options,
  ];
}

function formatPersonName(person: {
  name?: string;
  lastName?: string;
  displayName?: string;
}): string {
  return (
    person.displayName?.trim() ||
    `${person.name ?? ""} ${person.lastName ?? ""}`.trim() ||
    "Unknown"
  );
}

function formatGuestSpeakerName(guestSpeaker: {
  firstName?: string;
  lastName?: string;
}): string {
  return (
    `${guestSpeaker.firstName ?? ""} ${guestSpeaker.lastName ?? ""}`.trim() ||
    "Unknown"
  );
}

export function useRegistrantCatalogOptions({
  eventId,
  registrationData,
  isEditMode,
  patchRegistration,
}: UseRegistrantCatalogOptionsParams) {
  const dispatch = useDispatch<AppDispatch>();
  const eventData = useSelector(selectEventData);
  const staffFormState = useSelector(selectStaffFormState);
  const { autoPrepaidLocked } = useSelector(selectEditParticipantForm);

  const attendeeType = registrationData?.attendeeType;
  const isGuestType = isGuestRegistrantType(attendeeType);
  const organizationId = registrationData?.firmId;

  const { firmData } = UseFirmList();
  const [companyOptions, setCompanyOptions] = useState<
    RegistrantCatalogOption[]
  >([]);
  const [registrantOptions, setRegistrantOptions] = useState<
    RegistrantCatalogOption[]
  >([]);
  const [registeredPersonIds, setRegisteredPersonIds] = useState<Set<string>>(
    new Set(),
  );
  const personDetailsRef = useRef<
    Map<
      string,
      {
        name?: string;
        lastName?: string;
        email?: string;
        firm?: string;
        firmName?: string;
        mainOffice?: string;
        mainOfficeName?: string;
        additionalOffices?: string[];
      }
    >
  >(new Map());
  const guestSpeakerDetailsRef = useRef<
    Map<string, { firstName?: string; lastName?: string; email?: string }>
  >(new Map());

  const firmOptions = useMemo(
    () => filterFirmsForRegistrantType(firmData, attendeeType),
    [attendeeType, firmData],
  );

  useEffect(() => {
    if (!isGuestType) {
      return;
    }

    let cancelled = false;
    void getCompanies()
      .then((companies) => {
        if (cancelled) {
          return;
        }
        setCompanyOptions(
          companies.map((company) => ({
            id: company.id,
            name: company.name || "Unknown Company",
          })),
        );
      })
      .catch((error) => {
        console.error("Error loading companies:", error);
        if (!cancelled) {
          setCompanyOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isGuestType]);

  useEffect(() => {
    if (isEditMode || !eventId) {
      return;
    }

    let cancelled = false;
    void getRegistrationsByEvent(eventId)
      .then((registrations) => {
        if (cancelled) {
          return;
        }
        setRegisteredPersonIds(
          new Set(
            registrations
              .map((registration) => registration.personId)
              .filter((id): id is string => Boolean(id)),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setRegisteredPersonIds(new Set());
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, isEditMode]);

  useEffect(() => {
    if (isGuestType || !organizationId) {
      setRegistrantOptions([]);
      return;
    }

    let cancelled = false;
    void fetchPersonByFirm(organizationId)
      .then((persons) => {
        if (cancelled) {
          return;
        }

        const activePersons = (Array.isArray(persons) ? persons : []).filter(
          (person) => !isSoftDeletedPerson(person),
        );
        const formatted = activePersons.map((person) => ({
          id: person.id,
          name: formatPersonName(person),
          personName: person.name ?? "",
          personLastName: person.lastName ?? "",
          email: person.firmEmail ?? person.email ?? "",
          firm: person.firm ?? organizationId,
          firmName: person.firmName ?? registrationData?.firmName,
          mainOffice: person.mainOffice,
          mainOfficeName: person.mainOfficeName,
          additionalOffices: person.additionalOffices,
        }));

        personDetailsRef.current = new Map(
          formatted.map((person) => [
            person.id,
            {
              name: person.personName,
              lastName: person.personLastName,
              email: person.email,
              firm: person.firm,
              firmName: person.firmName,
              mainOffice: person.mainOffice,
              mainOfficeName: person.mainOfficeName,
              additionalOffices: person.additionalOffices,
            },
          ]),
        );

        const filtered = isEditMode
          ? formatted
          : formatted.filter((person) => !registeredPersonIds.has(person.id));

        setRegistrantOptions(filtered.map(({ id, name }) => ({ id, name })));
      })
      .catch((error) => {
        console.error("Error loading persons by firm:", error);
        if (!cancelled) {
          setRegistrantOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    isEditMode,
    isGuestType,
    organizationId,
    registeredPersonIds,
    registrationData?.firmName,
  ]);

  useEffect(() => {
    if (!isGuestType || !organizationId || !eventId) {
      if (isGuestType) {
        setRegistrantOptions([]);
      }
      return;
    }

    let cancelled = false;
    void getNotRegisteredGuestSpeakers(organizationId, eventId)
      .then((guestSpeakers) => {
        if (cancelled) {
          return;
        }
        guestSpeakerDetailsRef.current = new Map(
          guestSpeakers.map((guestSpeaker) => [
            guestSpeaker.id,
            {
              firstName: guestSpeaker.firstName,
              lastName: guestSpeaker.lastName,
              email: guestSpeaker.email,
            },
          ]),
        );
        setRegistrantOptions(
          guestSpeakers.map((guestSpeaker) => ({
            id: guestSpeaker.id,
            name: formatGuestSpeakerName(guestSpeaker),
          })),
        );
      })
      .catch((error) => {
        console.error("Error loading guest speakers:", error);
        if (!cancelled) {
          setRegistrantOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, isGuestType, organizationId]);

  const organizationOptions = useMemo(() => {
    const base = isGuestType ? companyOptions : firmOptions;
    return ensureSelectedOption(
      base,
      registrationData?.firmId,
      registrationData?.firmName,
    );
  }, [
    companyOptions,
    firmOptions,
    isGuestType,
    registrationData?.firmId,
    registrationData?.firmName,
  ]);

  const resolvedRegistrantOptions = useMemo(() => {
    const registrantName = [
      registrationData?.personName,
      registrationData?.personLastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    const selectedId = registrationData?.personId;
    const selectedStillAllowed =
      !selectedId || registrantOptions.some((option) => option.id === selectedId);
    return ensureSelectedOption(
      registrantOptions,
      selectedStillAllowed ? selectedId : undefined,
      registrantName,
    );
  }, [
    registrantOptions,
    registrationData?.personId,
    registrationData?.personLastName,
    registrationData?.personName,
  ]);

  const handleOrganizationChange = useCallback(
    (option: RegistrantCatalogOption | null) => {
      dispatch(setAutoPrepaidLocked(false));

      const basePatch: Partial<IRegistration> = option
        ? {
            firmId: option.id,
            firmName: option.longName || option.name,
            personId: "",
            personName: undefined,
            personLastName: undefined,
            personEmail: undefined,
            badgeName: "",
          }
        : {
            firmId: undefined,
            firmName: undefined,
            personId: "",
            personName: undefined,
            personLastName: undefined,
            personEmail: undefined,
            badgeName: "",
          };

      if (!isEditMode && registrationData && eventData) {
        patchRegistration({
          ...basePatch,
          ...clearStaffPrepaidSelection(
            { ...registrationData, ...basePatch },
            eventData,
            staffFormState,
          ),
        });
        return;
      }

      patchRegistration(basePatch);
    },
    [
      dispatch,
      eventData,
      isEditMode,
      patchRegistration,
      registrationData,
      staffFormState,
    ],
  );

  const handleRegistrantChange = useCallback(
    (option: RegistrantCatalogOption | null) => {
      if (!option) {
        dispatch(setAutoPrepaidLocked(false));

        const clearedPersonPatch: Partial<IRegistration> = {
          personId: "",
          personName: undefined,
          personLastName: undefined,
          personEmail: undefined,
          badgeName: "",
        };

        if (!isEditMode && registrationData && eventData) {
          patchRegistration({
            ...clearedPersonPatch,
            ...clearStaffPrepaidSelection(
              { ...registrationData, ...clearedPersonPatch },
              eventData,
              staffFormState,
            ),
          });
        } else {
          patchRegistration(clearedPersonPatch);
        }
        return;
      }

      if (isGuestType) {
        const guestSpeaker = guestSpeakerDetailsRef.current.get(option.id);
        const firstName =
          guestSpeaker?.firstName ?? option.name.split(" ")[0] ?? "";
        const lastName =
          guestSpeaker?.lastName ??
          option.name.split(" ").slice(1).join(" ") ??
          "";

        patchRegistration({
          personId: option.id,
          personName: firstName,
          personLastName: lastName,
          personEmail: guestSpeaker?.email,
          badgeName: "",
        });
        return;
      }

      const person = personDetailsRef.current.get(option.id);
      const personName = person?.name ?? option.name.split(" ")[0] ?? "";
      const personLastName =
        person?.lastName ?? option.name.split(" ").slice(1).join(" ") ?? "";

      const personPatch: Partial<IRegistration> = {
        personId: option.id,
        personName,
        personLastName,
        personEmail: person?.email,
        firmId: person?.firm ?? organizationId,
        firmName: person?.firmName ?? registrationData?.firmName,
        mainOffice: person?.mainOffice,
        mainOfficeName: person?.mainOfficeName,
        additionalOffices: person?.additionalOffices,
        badgeName: "",
      };

      patchRegistration(personPatch);

      if (
        isEditMode ||
        !eventId ||
        !registrationData ||
        !eventData ||
        registrationData.isHostFirm === true ||
        !isBillableMemberType(registrationData.attendeeType) ||
        !isAnnualMeetingEvent(eventData, registrationData)
      ) {
        return;
      }

      void (async () => {
        try {
          const mainOfficeId = person?.mainOffice?.trim();
          if (mainOfficeId) {
            const office = await getOfficeById(mainOfficeId);
            if (office?.showInDirectory !== true) {
              if (autoPrepaidLocked) {
                dispatch(setAutoPrepaidLocked(false));
                patchRegistration(
                  clearStaffPrepaidSelection(
                    { ...registrationData, ...personPatch },
                    eventData,
                    staffFormState,
                  ),
                );
              }
              return;
            }
          }

          const isPrepaid = await checkPrepaidStatus(eventId, option.id);

          if (isPrepaid) {
            dispatch(setAutoPrepaidLocked(true));
            patchRegistration(applyStaffPrepaidSelection());
            return;
          }

          if (autoPrepaidLocked) {
            dispatch(setAutoPrepaidLocked(false));
            patchRegistration(
              clearStaffPrepaidSelection(
                { ...registrationData, ...personPatch },
                eventData,
                staffFormState,
              ),
            );
          }
        } catch (error) {
          console.error("Error checking prepaid status for registrant:", error);
        }
      })();
    },
    [
      autoPrepaidLocked,
      dispatch,
      eventData,
      eventId,
      isEditMode,
      isGuestType,
      organizationId,
      patchRegistration,
      registrationData,
      staffFormState,
    ],
  );

  return {
    isGuestRegistrantType: isGuestType,
    organizationLabel: isGuestType ? "Company" : "Firm",
    organizationOptions,
    organizationValue: registrationData?.firmId ?? null,
    registrantOptions: resolvedRegistrantOptions,
    registrantValue: registrationData?.personId ?? null,
    handleOrganizationChange,
    handleRegistrantChange,
  };
}
